import React, { useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@apollo/client'
import { Button, Image, Popconfirm, Space, Upload } from 'antd'
import { DeleteOutlined, FileOutlined, UploadOutlined } from '@ant-design/icons'

import { initActiveStorageBlobs } from '../../helpers/directUpload'

import { DELETE_UNATTACHED_BLOBS_MUTATION } from './graphql'

const THUMBNAIL_SIZE = 44

export const fileShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  filename: PropTypes.string.isRequired,
  contentType: PropTypes.string,
  url: PropTypes.string,
  thumbnailUrl: PropTypes.string
})

export const FileThumb = ({ file }) => {
  if (file.thumbnailUrl) {
    return (
      <Image
        alt={file.filename}
        height={THUMBNAIL_SIZE}
        preview={file.url ? { src: file.url } : false}
        src={file.thumbnailUrl}
        style={{ objectFit: 'cover' }}
        width={THUMBNAIL_SIZE}
      />
    )
  }

  return (
    <a href={file.url} rel='noopener noreferrer' target='_blank'>
      <FileOutlined /> {file.filename}
    </a>
  )
}

FileThumb.propTypes = {
  file: fileShape.isRequired
}

export const FileThumbList = ({ files }) => {
  if (!files || files.length === 0) return '-'

  return (
    <Space size={4} wrap>
      {files.map(file => <FileThumb file={file} key={file.id} />)}
    </Space>
  )
}

FileThumbList.propTypes = {
  files: PropTypes.arrayOf(fileShape)
}

FileThumbList.defaultProps = {
  files: []
}

export const AttachedFiles = ({ files, onRemove, removingId, disabled }) => {
  const { t } = useTranslation('employeeContracts')

  if (!files || files.length === 0) return null

  return (
    <Space direction='vertical' style={{ marginBottom: 8, width: '100%' }}>
      {files.map(file => (
        <Space key={file.id}>
          <FileThumb file={file} />
          {file.thumbnailUrl && <span>{file.filename}</span>}
          <Popconfirm
            cancelText={t('cancel')}
            disabled={disabled}
            okText={t('removeFile')}
            onConfirm={() => onRemove(file)}
            title={t('removeFileConfirm')}
          >
            <Button
              danger
              disabled={disabled}
              icon={<DeleteOutlined />}
              loading={removingId === file.id}
              size='small'
              type='text'
            />
          </Popconfirm>
        </Space>
      ))}
    </Space>
  )
}

AttachedFiles.propTypes = {
  disabled: PropTypes.bool,
  files: PropTypes.arrayOf(fileShape),
  onRemove: PropTypes.func.isRequired,
  removingId: PropTypes.string
}

AttachedFiles.defaultProps = {
  disabled: false,
  files: [],
  removingId: null
}

export const SignedIdUploader = ({ onChange, onUploadingChange, disabled }) => {
  const { t } = useTranslation('employeeContracts')
  const [fileList, setFileList] = useState([])
  const blobsByUid = useRef({})
  const removedUids = useRef(new Set())
  const [deleteBlobs] = useMutation(DELETE_UNATTACHED_BLOBS_MUTATION)

  const emitSignedIds = () => {
    onChange(Object.values(blobsByUid.current).map(blob => blob.signed_id))
  }

  const handleChange = ({ fileList: nextFileList }) => {
    setFileList(nextFileList)
    onUploadingChange(nextFileList.some(file => file.status === 'uploading'))
  }

  const customRequest = ({ file, onSuccess, onError }) => {
    initActiveStorageBlobs([file], {
      onSuccess: (blob) => {
        if (removedUids.current.has(file.uid)) {
          deleteBlobs({ variables: { blobIds: [blob.id] } }).catch(() => {})
          return
        }

        blobsByUid.current = { ...blobsByUid.current, [file.uid]: blob }
        emitSignedIds()
        onSuccess('Ok')
      },
      onError: () => onError(new Error(t('uploadFailed')))
    })

    return { abort () {} }
  }

  const handleRemove = (file) => {
    removedUids.current.add(file.uid)
    const blob = blobsByUid.current[file.uid]
    if (!blob) return true

    const { [file.uid]: _removed, ...rest } = blobsByUid.current
    blobsByUid.current = rest
    emitSignedIds()
    deleteBlobs({ variables: { blobIds: [blob.id] } }).catch(() => {})

    return true
  }

  return (
    <Upload
      customRequest={customRequest}
      disabled={disabled}
      fileList={fileList}
      listType='picture'
      multiple
      onChange={handleChange}
      onRemove={handleRemove}
    >
      <Button disabled={disabled} icon={<UploadOutlined />}>{t('upload')}</Button>
    </Upload>
  )
}

SignedIdUploader.propTypes = {
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  onUploadingChange: PropTypes.func
}

SignedIdUploader.defaultProps = {
  disabled: false,
  onChange: () => {},
  onUploadingChange: () => {}
}
